import React, { useEffect, useState } from 'react';
import {
  listPlatformAdmins,
  addPlatformAdmin,
  removePlatformAdmin,
  type PlatformAdminRow,
} from './api';

export default function Admins({ toast }: { toast: (m: string) => void }) {
  const [admins, setAdmins] = useState<PlatformAdminRow[]>([]);
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    setAdmins(await listPlatformAdmins());
    setLoading(false);
  };
  useEffect(() => {
    void load();
  }, []);

  const add = async () => {
    const value = email.trim();
    if (!value) return;
    setBusy(true);
    try {
      const result = await addPlatformAdmin(value);
      if (result === 'ok') {
        toast(`${value} is now a platform admin`);
        setEmail('');
        await load();
      } else if (result === 'not_found') {
        toast(`No AssetWRX account found for ${value}. They must sign in to the app once first.`);
      } else {
        toast(`${value} is already an admin`);
      }
    } catch (e) {
      toast(`Could not add: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (row: PlatformAdminRow) => {
    setBusy(true);
    try {
      await removePlatformAdmin(row.user_id);
      toast(`Removed ${row.email ?? 'admin'}`);
      await load();
    } catch (e) {
      toast(`Could not remove: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ display: 'grid', gap: 18, maxWidth: 720 }}>
      <section className="card">
        <h2 style={{ marginTop: 0 }}>Add a platform admin</h2>
        <p className="sub" style={{ marginBottom: 10 }}>
          Platform admins can open this portal and change what each edition includes. Enter the person&apos;s
          email. They must already have an AssetWRX account - i.e. they have signed in to the app at least once.
        </p>
        <div className="row" style={{ alignItems: 'flex-end', gap: 10 }}>
          <div style={{ flex: 1 }}>
            <label>Email</label>
            <input
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="person@company.com"
              onKeyDown={(e) => e.key === 'Enter' && void add()}
            />
          </div>
          <button className="btn primary" disabled={busy} onClick={() => void add()}>
            {busy ? 'Adding…' : 'Add admin'}
          </button>
        </div>
      </section>

      <section className="card">
        <h2 style={{ marginTop: 0 }}>Current platform admins</h2>
        {loading ? (
          <p className="muted">Loading…</p>
        ) : admins.length === 0 ? (
          <p className="muted">No admins found.</p>
        ) : (
          <table className="matrix" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th>Email</th>
                <th>Added</th>
                <th className="col-ed"></th>
              </tr>
            </thead>
            <tbody>
              {admins.map((a) => (
                <tr key={a.user_id}>
                  <td className="feat-name">{a.email ?? a.user_id}</td>
                  <td className="muted">{new Date(a.created_at).toLocaleDateString()}</td>
                  <td className="col-ed">
                    <button className="btn" disabled={busy || admins.length <= 1} onClick={() => void remove(a)}>
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <p className="hint" style={{ marginTop: 10 }}>
          The very first admin is created directly in the database. You can add everyone else here. The last
          remaining admin can&apos;t be removed (so you never lock yourself out).
        </p>
      </section>
    </div>
  );
}
